/**
 * Theme Configuration
 *
 * Provides theme selection logic for both web and Teams applications.
 * Maps Teams themes to appropriate Fluent UI themes.
 */

import type { Theme } from '@fluentui/react-components';
import {
  webLightTheme,
  webDarkTheme,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
} from '@fluentui/react-components';
import type { TeamsTheme } from '@/contexts/TeamsContext';

/**
 * Get the appropriate Fluent UI theme based on Teams theme
 * @param teamsTheme - The current Teams theme
 * @returns Fluent UI Theme object
 */
export function getTeamsFluentTheme(teamsTheme: TeamsTheme): Theme {
  switch (teamsTheme) {
    case 'dark':
      return teamsDarkTheme;
    case 'contrast':
      return teamsHighContrastTheme;
    case 'default':
    default:
      return teamsLightTheme;
  }
}

/**
 * Get the web app theme (defaults to light theme)
 * Can be extended to support user preferences or system theme detection
 * @returns Fluent UI Theme object
 */
export function getWebTheme(): Theme {
  // Check if user prefers dark mode (system preference)
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

  return prefersDark ? webDarkTheme : webLightTheme;
}

/**
 * Check if the system prefers dark mode
 * @returns true if dark mode is preferred
 */
export function systemPrefersDarkMode(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Check if the system prefers high contrast
 * @returns true if high contrast is preferred
 */
export function systemPrefersHighContrast(): boolean {
  return window.matchMedia('(prefers-contrast: high)').matches;
}
